<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("clients") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="numberOfItems"
					:currentPage="computedPage"
				/>
			</template>
			<!-- Display this only if it's in tenants settings -->
			<template slot="actions">
				<j-button
					v-can:or="['writeIndividuals', 'writeCompanies']"
					:to="`/${$route.params.tenantId}/clients/create`"
					type="primary"
				>
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create_client") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search and quick filter -->
		<div class="flex wrap xs-12 xs-left">
			<!-- Search -->
			<div class="flex xs-12 md-5">
				<template v-if="!$store.state.clients.listing.filter.isFiltering">
					<j-base-input-search
						:placeholder="$t('search_name')"
						:value="computedSearchTerm"
						@input="onSearch($event)"
					/>
					<j-button
						class="ml2"
						v-if="computedSearchTerm"
						type="tertiary"
						@click="cancelSearch"
						>{{ $t("cancel") }}</j-button
					>
				</template>
			</div>

			<!-- Filter toggle  -->
			<div class="flex xs-12 md-7 xs-middle xs-right pt2 pb2">
				<label for="toggle" class="mr2 c6">
					<template>{{ $t("filter") }}</template>
				</label>
				<j-base-toggle
					id="toggle"
					size="small"
					:value="$store.state.clients.listing.filter.isFiltering"
					@input="handleFitlerOnOff"
				/>
			</div>
		</div>

		<!-- Filter -->
		<div class="xs-12">
			<core-filter
				:isExpanded="$store.state.clients.listing.filter.isFiltering"
				:value="$store.state.clients.listing.filter.payload"
				:forceReset="forceFilterReset"
				for="individuals"
				@submit="handleFilterSubmit"
			/>
		</div>

		<!-- Tabs panel -->
		<div class="flex wrap xs-12 mt2">
			<!-- Tabs -->
			<div class="flex xs-12 md-4 xs-left xs-middle">
				<j-tabs
					:selectedTab="computedTab"
					:tabs="[
						{
							name: $t('individuals'),
							value: 'individual'
						},
						{
							name: $t('companies'),
							value: 'company'
						}
					]"
					@click="onTabChange($event)"
				/>
			</div>
		</div>

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="computedHeader"
				:list="computedList"
				:sortable="true"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in computedHeader" :key="i">
						<template v-if="i === 0">
							<router-link :to="decideClientLink(item.id)">{{
								item[column.field]
							}}</router-link>
						</template>
						<template v-else-if="column.field === 'actions'">
							<crm-icon
								name="delete"
								color="cError"
								@click.native="showDeleteDialog(item.id)"
							/>
						</template>
						<template v-else>{{ item[column.field] }}</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="numberOfItems"
				:perPage="itemsPerPage"
				:activePage="computedPage"
				@click="onPageChange($event)"
			/>
		</div>

		<!-- Delete Confirmation Modal -->
		<j-dialog
			:title="$t('delete_client')"
			type="delete"
			@confirm="onDelete(selectedId)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_client") }}</j-dialog
		>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CrmIcon from "@/components/CrmIcon/"
import JHeadline from "@/components/JHeadline/"
import JText from "@/components/JText/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JAvatar from "@/components/JAvatar/"
import JSearch from "@/components/JSearch/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JBaseToggle from "@/components/JBaseToggle/"
import JPagination from "@/components/JPagination/"
import JTabs from "@/components/JTabs/"
import JDialog from "@/components/JDialog/"
import CoreFilter from "@/modules/core/components/CoreFilter/"
import { mapState } from "vuex"
import dayjs from "dayjs"
import get from "lodash.get"
import debounce from "lodash.debounce"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CorePaginationInfo,
		CrmIcon,
		JText,
		JButton,
		JHeadline,
		JCard,
		JDataTable,
		JAvatar,
		JSearch,
		JPagination,
		JTabs,
		JDialog,
		JBaseInputSearch,
		JBaseToggle,
		CoreFilter
	},

	data() {
		return {
			isLoading: false,

			// Fetched data
			numberOfItems: 0,
			clients: [],

			// Pagination
			itemsPerPage: 30,

			// Filtering
			filter: {
				where: null // processed payload into where param of graphql query
			},

			forceFilterReset: 0,

			selectedId: null
		}
	},

	computed: {
		computedPage() {
			return this.$store.state.clients.listing.page
		},

		computedWhere() {
			return this.$store.state.clients.listing.filter.where
		},

		computedSearchTerm() {
			return this.$store.state.clients.listing.searchTerm
		},

		computedSorting() {
			return this.$store.state.clients.listing.sorting
		},

		computedHeader() {
			return this.$store.state.clients.listing.header
		},

		computedTab() {
			return this.$store.state.clients.listing.tab
		},

		// What we are showing to user
		computedList() {
			const extractFromArray = (type, key, item) => {
				if (item[key]) {
					const foundItem = item[key].find(item => item.type === type)
					return foundItem ? foundItem.value : null
				}

				return null
			}

			/**
			 * Helper function to format date
			 */
			const formatDate = date =>
				date ? dayjs(date).format(this.$store.state.settings.dateFormat) : null

			/**
			 * Return computed list
			 */

			if (this.computedTab === "individual")
				return this.clients.map(item => {
					return {
						...item,
						type:
							item.__typename === "individual"
								? this.$t("individual")
								: this.$t("company"),
						dateOfBirth: formatDate(item.dateOfBirth),
						email: item?.email,
						telephoneNumber: extractFromArray(
							"telephoneNumber",
							"contacts",
							item
						),
						source: item.source ? this.$t(`sources.${item.source}`) : null,
						createdAt: dayjs(item.createdAt).format("DD/MM/YYYY HH:mm:ss"),
						updatedAt: formatDate(item.lastModifiedAt),
						"createdBy.associatedUser.name":
							item.createdBy && item.createdBy.associatedUser
								? item.createdBy.associatedUser.name
								: null,
						"lastModifiedBy.associatedUser.name":
							item.lastModifiedBy && item.lastModifiedBy.associatedUser
								? item.lastModifiedBy.associatedUser.name
								: null
					}
				})
			// for company
			else
				return this.clients.map(item => {
					return {
						type: item.__typename,
						id: item.id,
						name: item.name,
						email: extractFromArray("email", "contacts", item)
							? extractFromArray("email", "contacts", item)
							: extractFromArray("Email", "contacts", item),
						natureOfBusiness: item.natureOfBusiness,
						registrationNumber: item.registrationNumber,
						createdAt: dayjs(item.createdAt).format("DD/MM/YYYY HH:mm:ss")
					}
				})
		}
	},

	methods: {
		buildHeader() {
			let header = null
			if (this.computedTab === "individual")
				header = [
					{
						field: "name",
						label: this.$t("name"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "email",
						label: this.$t("email"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "dateOfBirth",
						label: this.$t("date_of_birth"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "createdAt",
						label: this.$t("created"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "actions",
						label: this.$t("actions"),
						sortable: false
					}
				]
			if (this.computedTab === "company")
				header = [
					{
						field: "name",
						label: this.$t("name"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "email",
						label: this.$t("email"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "natureOfBusiness",
						label: this.$t("nature_of_business"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "registrationNumber",
						label: this.$t("registration_number"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "createdAt",
						label: this.$t("created"),
						isSorting: false,
						direction: "desc"
					},
					{
						field: "actions",
						label: this.$t("actions"),
						sortable: false
					}
				]
			return header
		},

		onTabChange(tab) {
			this.$store.commit("clients/SET_TAB", tab)

			const header = this.buildHeader()

			this.$store.commit("clients/SET_HEADER", header)

			// refetch clients
			this.fetchClients()

			// reset page to 1
			this.$store.commit("clients/SET_PAGE", 1)
			// Turn filter off and clear filters
			this.handleFitlerOnOff(false)
			// clear search
			this.$store.commit("clients/SET_SEARCH_TERM", null)
			//reset sorting
			this.$store.commit("clients/RESET_SORTING")
		},

		onSort(payload) {
			//update data table header
			this.$store.commit("clients/SET_HEADER", payload)

			// find sort object which is active
			let activeSort = payload.filter(item => item.isSorting === true)

			this.$store.commit("clients/SET_SORTING", {
				fieldName: activeSort[0].field,
				type: activeSort[0].direction
			})

			// reset page to 1
			this.$store.commit("clients/SET_PAGE", 1)

			this.fetchClients()
		},

		onPageChange(pageIndex) {
			this.$store.commit("clients/SET_PAGE", pageIndex)
			this.fetchClients()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value) {
			const payload = value === null || value === "" ? null : value
			this.$store.commit("clients/SET_SEARCH_TERM", value)
			this.$store.commit("clients/SET_PAGE", 1)

			let where = {
				and: [{ type_in: "CUSTOMER" }, { name_contains: value }]
			}

			this.$store.commit("clients/SET_FILTER_WHERE", where)

			this.fetchClients()
		}, 200),

		cancelSearch() {
			this.$store.commit("clients/SET_SEARCH_TERM", null)
			this.$store.commit("clients/SET_PAGE", 1)
			this.onSearch()
		},

		handleFitlerOnOff(value) {
			this.$store.commit("clients/SET_FILTER_IS_FILTERING", value)

			// When we turn off the filter, refetch
			if (value === false) {
				++this.forceFilterReset
				this.$store.commit("clients/SET_PAGE", 1)
				this.$store.commit("clients/SET_FILTER_PAYLOAD", null)
				this.$store.commit("clients/SET_FILTER_WHERE", {
					type_in: "CUSTOMER"
				})
				this.fetchClients()
			}
		},

		handleFilterSubmit({ payload, where }) {
			this.$store.commit("clients/SET_FILTER_PAYLOAD", payload)
			this.$store.commit("clients/SET_FILTER_WHERE", where)
			this.$store.commit("clients/SET_PAGE", 1)

			this.fetchClients()
		},

		async fetchClients() {
			if (this.computedTab === "individual") this.fetchIndividuals()
			else this.fetchCompanies()
		},

		async fetchIndividuals() {
			const query = gql`
				query individual(
					$where: individualWhereInput
					$skip: Int
					$limit: Int
					$sort: sortInput
				) {
					individuals(where: $where, skip: $skip, limit: $limit, sort: $sort) {
						totalCount
						list {
							id
							name
							email
							dateOfBirth
							createdAt
						}
					}
				}
			`

			const { itemsPerPage } = this

			// Sort out sorting (haha)
			let sort = this.computedSorting

			// Pagination
			const skip = (this.computedPage - 1) * itemsPerPage
			const limit = itemsPerPage

			// Build filter aka `where`
			// Either search is active or filter is active, not both
			let where = this.computedWhere

			const variables = {
				where,
				skip,
				limit,
				sort
			}

			this.isLoading = true

			const res = await api({ query, variables })

			this.isLoading = false

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			// Success
			this.numberOfItems = res.data?.individuals?.totalCount
			this.clients = res.data?.individuals?.list
		},

		async fetchCompanies() {
			const query = gql`
				query company(
					$where: companyWhereInput
					$skip: Int
					$limit: Int
					$sort: sortInput
				) {
					companies(where: $where, skip: $skip, limit: $limit, sort: $sort) {
						totalCount
						list {
							id
							name
							contacts {
								id
								type
								value
							}
							registrationNumber
							natureOfBusiness
							createdAt
						}
					}
				}
			`

			const { itemsPerPage } = this

			// Sort out sorting (haha)
			let sort = this.computedSorting

			// Pagination
			const skip = (this.computedPage - 1) * itemsPerPage
			const limit = itemsPerPage

			// Build filter aka `where`
			// Either search is active or filter is active, not both
			let where = this.computedWhere

			const variables = {
				where,
				skip,
				limit,
				sort
			}

			this.isLoading = true

			const res = await api({ query, variables })

			this.isLoading = false

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			// Success
			this.numberOfItems = res.data?.companies?.totalCount
			this.clients = res.data?.companies?.list
		},

		/**
		 * When policy is issued, we do link to new modules
		 * When policy is not issued, we do link to old pages
		 * TODO: it's a hack for now, we are changing how policies are displayed
		 * once policies are only issued policies, this should be removed
		 */
		decideClientLink(id) {
			if (this.computedTab === "individual")
				return `/${this.$store.state.tenantId}/clients/detail/${id}/overview/individual`
			else
				return `/${this.$store.state.tenantId}/clients/detail/${id}/overview/company`
		},

		showDeleteDialog(id) {
			this.$modal.show("j-dialog")
			this.selectedId = id
		},

		async onDelete(id) {
			const query = `
        mutation deleteCustomer($id: String!){
          deleteEntity(id: $id){
            status
            errors
          }
        }
      `

			this.isLoading = true
			const res = await api({ query, variables: { id } })
			this.isLoading = false

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			this.fetchClients()

			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("client_removed")
				}
			})
		}
	},

	beforeRouteLeave(to, from, next) {
		// If in same module , do nothing, do not reset page
		if (to.meta.featureId === from.meta.featureId) {
			next()
		} else {
			// if not in same module

			//reset page to 1
			this.$store.commit("clients/SET_PAGE", 1)
			// Turn filter off and clear filters
			this.handleFitlerOnOff(false)
			// clear search
			this.$store.commit("clients/SET_SEARCH_TERM", null)
			//reset sorting
			this.$store.commit("clients/RESET_SORTING")
			//reset header
			this.$store.commit("clients/RESET_HEADER")
			// set tab to individual
			this.$store.commit("clients/SET_TAB", "individual")

			next()
		}
	},

	created() {
		const header = this.buildHeader()
		this.$store.commit("clients/SET_HEADER", header)
		this.fetchClients()
	}
}
</script>
